import { areSurvivorGameplayEquivalents } from './survivorPerkEquivalents'
import {
  functionalRedundancyGroups,
  functionalRedundancyPolicy,
  getSurvivorCentralProfile,
  getSurvivorCentralWantWeight,
  getSurvivorPerkRoleIds,
  getSurvivorRoleRecipeMatches
} from './survivorPerkRoles'
import { getSurvivorInteraction } from './survivorPerkInteractions'

const GENERIC_TAGS = new Set([
  'aura',
  'chase',
  'escape',
  'information',
  'macro',
  'solo_queue',
  'support',
  'teamplay'
])

const CONFIDENCE_LABELS = {
  exact: 'Exact mechanic',
  curated: 'Curated link',
  supported: 'Role-supported',
  inferred: 'Inferred fit'
}

function clampScore (score) {
  return Math.max(0, Math.min(100, Math.round(score || 0)))
}

function perkId (perkOrId) {
  return typeof perkOrId === 'string' ? perkOrId : perkOrId && perkOrId.id
}

function perkName (perkOrId) {
  if (typeof perkOrId === 'string') return perkOrId.replace(/_/g, ' ')
  return (perkOrId && (perkOrId.displayName || perkOrId.id)) || 'perk'
}

function directionalWeight (sourcePerk, targetId) {
  if (!sourcePerk || !sourcePerk.synergyWeights) return 0
  return sourcePerk.synergyWeights[targetId] || 0
}

function explicitAntiSynergy (sourcePerk, targetId) {
  if (!sourcePerk || !sourcePerk.antiSynergies) return null
  return sourcePerk.antiSynergies[targetId] || null
}

function sharedValues (first, second) {
  const secondSet = new Set(second || [])
  return Array.from(new Set((first || []).filter(value => secondSet.has(value))))
}

function usefulSharedTags (firstPerk, secondPerk) {
  return sharedValues(firstPerk && firstPerk.tags, secondPerk && secondPerk.tags)
    .filter(tag => !GENERIC_TAGS.has(String(tag).toLowerCase()))
}

function redundancyDetails (firstId, secondId) {
  const groupId = Object.keys(functionalRedundancyGroups).find(id => {
    const group = functionalRedundancyGroups[id] || []
    return group.indexOf(firstId) !== -1 && group.indexOf(secondId) !== -1
  })
  if (!groupId) return null
  const policy = functionalRedundancyPolicy[groupId] || { penaltyPerExtra: 16 }
  return {
    groupId,
    penalty: policy.penaltyPerExtra || 16
  }
}

function forbiddenRoleOverlap (centralId, candidateId) {
  const profile = getSurvivorCentralProfile(centralId)
  if (!profile || !profile.forbiddenRoles || !profile.forbiddenRoles.length) return []
  const candidateRoles = getSurvivorPerkRoleIds(candidateId)
  return candidateRoles.filter(role => profile.forbiddenRoles.indexOf(role) !== -1)
}

function recipeEvidence (centralId, candidateId) {
  return getSurvivorRoleRecipeMatches(centralId, candidateId)
    .slice()
    .sort((first, second) => second.weight - first.weight)[0] || null
}

function result (data) {
  const confidence = data.confidence || 'inferred'
  return Object.assign({
    score: 0,
    kind: 'independent',
    blocked: false,
    confidence,
    confidenceLabel: CONFIDENCE_LABELS[confidence],
    source: 'inferred',
    reason: 'No direct interaction is documented; the fit is estimated from complementary roles.',
    advantages: [],
    drawbacks: []
  }, data, {
    score: clampScore(data.score),
    confidence,
    confidenceLabel: CONFIDENCE_LABELS[confidence]
  })
}

/**
 * Explainable compatibility between two Survivor perks.
 *
 * Exact mechanic contracts always win. Curated pair/central weights are the
 * next strongest evidence; recipes and tags may fill catalogue gaps, but are
 * deliberately marked as lower-confidence inference.
 */
export function evaluateSurvivorPerkCompatibility (firstPerk, secondPerk) {
  const firstId = perkId(firstPerk)
  const secondId = perkId(secondPerk)
  if (!firstId || !secondId) return result({ reason: 'Compatibility data is unavailable for this perk.' })

  if (firstId === secondId || areSurvivorGameplayEquivalents(firstId, secondId)) {
    return result({
      kind: 'duplicate',
      blocked: true,
      confidence: 'exact',
      source: 'gameplay-identity',
      reason: 'These entries represent the same gameplay effect and cannot add separate value.',
      drawbacks: ['Duplicate gameplay effect.']
    })
  }

  const mechanic = getSurvivorInteraction(firstPerk, secondPerk)
  if (mechanic) {
    const positiveMechanic = ['synergy', 'conditional_synergy', 'state_coverage'].indexOf(mechanic.kind) !== -1
    return result({
      score: mechanic.weight,
      kind: mechanic.kind,
      blocked: Boolean(mechanic.bestBuildBlocked),
      confidence: 'exact',
      source: mechanic.source || 'mechanic-contract',
      reason: mechanic.reason,
      advantages: positiveMechanic ? ['Verified mechanical interaction.'] : [],
      drawbacks: positiveMechanic ? [] : [mechanic.reason]
    })
  }

  const firstAnti = explicitAntiSynergy(firstPerk, secondId)
  const secondAnti = explicitAntiSynergy(secondPerk, firstId)
  const anti = firstAnti || secondAnti
  if (anti) {
    return result({
      kind: 'anti_synergy',
      blocked: anti.severity === 'hard',
      confidence: 'exact',
      source: 'explicit-anti-synergy',
      reason: anti.reason || 'One perk prevents the other from delivering its intended value.',
      drawbacks: [anti.reason || 'Mechanics conflict.']
    })
  }

  const pairWeight = Math.max(
    directionalWeight(firstPerk, secondId),
    directionalWeight(secondPerk, firstId)
  )
  const centralWeight = Math.max(
    getSurvivorCentralWantWeight(firstId, secondId) || 0,
    getSurvivorCentralWantWeight(secondId, firstId) || 0
  )
  const firstRecipe = recipeEvidence(firstId, secondId)
  const secondRecipe = recipeEvidence(secondId, firstId)
  const recipes = [firstRecipe, secondRecipe].filter(Boolean).sort((first, second) => second.weight - first.weight)
  const recipe = recipes[0] || null
  const recipeWeight = recipe ? recipe.weight : 0
  const sharedRoles = sharedValues(getSurvivorPerkRoleIds(firstId), getSurvivorPerkRoleIds(secondId))
  const sharedTags = usefulSharedTags(firstPerk, secondPerk)
  const redundancy = redundancyDetails(firstId, secondId)
  const forbiddenRoles = Array.from(new Set(
    forbiddenRoleOverlap(firstId, secondId).concat(forbiddenRoleOverlap(secondId, firstId))
  ))
  const strongestCuratedWeight = Math.max(pairWeight, centralWeight)
  const inferredWeight = Math.max(
    recipeWeight,
    sharedRoles.length ? 38 + Math.min(3, sharedRoles.length) * 7 : 0,
    sharedTags.length ? 32 + Math.min(3, sharedTags.length) * 6 : 0
  )
  const redundancyPenalty = redundancy ? Math.min(26, redundancy.penalty) : 0
  const forbiddenPenalty = forbiddenRoles.length ? 24 : 0
  const score = Math.max(strongestCuratedWeight, inferredWeight) - redundancyPenalty - forbiddenPenalty
  const advantages = []
  const drawbacks = []
  let confidence = 'inferred'
  let source = 'role-and-tag-inference'
  let reason = 'No direct interaction is documented; the fit is estimated from complementary roles.'

  if (strongestCuratedWeight > 0) {
    confidence = 'curated'
    source = centralWeight >= pairWeight ? 'central-wants' : 'curated-pair'
    reason = centralWeight >= pairWeight
      ? `${perkName(secondPerk)} supports the intended game plan of ${perkName(firstPerk)} (or the reverse).`
      : 'A curated gameplay link connects these two perks.'
    advantages.push('Curated compatibility link.')
  } else if (recipe) {
    confidence = recipe.generated ? 'inferred' : 'supported'
    source = recipe.generated ? 'generated-role-recipe' : 'role-recipe'
    reason = `The perk fills the ${recipe.slotName.replace(/_/g, ' ')} job in the matching build recipe.`
    advantages.push(`Covers ${recipe.candidateRole.replace(/_/g, ' ')}.`)
  } else if (sharedRoles.length) {
    reason = `Both perks contribute to ${sharedRoles.slice(0, 2).join(' / ').replace(/_/g, ' ')}; this is role similarity, not a guaranteed mechanic combo.`
    advantages.push('Shares a relevant gameplay role.')
  } else if (sharedTags.length) {
    reason = `The fit is inferred from ${sharedTags.slice(0, 2).join(' / ')} tags; no direct mechanic is documented.`
    advantages.push('Fits the same gameplay theme.')
  }

  if (redundancy) {
    drawbacks.push(`Possible overlap: both perks occupy the ${redundancy.groupId.replace(/_/g, ' ')} function.`)
  }
  if (forbiddenRoles.length) {
    drawbacks.push(`The ${forbiddenRoles.join(' / ').replace(/_/g, ' ')} role conflicts with the central profile.`)
  }

  const kind = redundancy
    ? 'redundancy'
    : strongestCuratedWeight > 0
      ? 'curated_fit'
      : recipe || sharedRoles.length
        ? 'role_fit'
        : sharedTags.length
          ? 'theme'
          : 'compatible'

  return result({
    score,
    kind,
    blocked: false,
    confidence,
    source,
    reason,
    advantages,
    drawbacks
  })
}

export function summarizeSurvivorBuildCompatibility (candidatePerk, selectedPerks, centralPerkId) {
  const comparisons = (selectedPerks || [])
    .filter(perk => perk && perkId(perk) !== perkId(candidatePerk))
    .map(perk => Object.assign({ withPerkId: perkId(perk), withPerkName: perkName(perk) }, evaluateSurvivorPerkCompatibility(perk, candidatePerk)))
  const blocked = comparisons.find(comparison => comparison.blocked)
  if (blocked) return Object.assign({}, blocked, { comparisons })
  if (!comparisons.length) {
    return result({
      score: 100,
      kind: 'central',
      confidence: 'exact',
      source: 'central',
      reason: 'Selected as the build identity anchor.'
    })
  }

  const central = comparisons.find(comparison => comparison.withPerkId === centralPerkId)
  const ranked = comparisons.slice().sort((first, second) => second.score - first.score)
  const best = ranked[0]
  const average = comparisons.reduce((sum, comparison) => sum + comparison.score, 0) / comparisons.length
  const score = central
    ? central.score * 0.58 + average * 0.27 + best.score * 0.15
    : average * 0.65 + best.score * 0.35
  const drawbacks = Array.from(new Set(comparisons.reduce((all, comparison) => all.concat(comparison.drawbacks || []), [])))

  return result({
    score,
    kind: best.kind,
    blocked: false,
    confidence: best.confidence,
    source: best.source,
    reason: best.reason,
    advantages: best.advantages || [],
    drawbacks,
    comparisons
  })
}

export default evaluateSurvivorPerkCompatibility
